/**
 * Una linea del remito, con la pregunta que el sistema no pudo contestar.
 *
 * DUDOSA: matcheo por parecido de nombre. "¿Es tu «X»?" — Si / No, es otro /
 * No lo tengo, crearlo / Dejar afuera.
 * SIN_MATCH: no hay producto. "No lo tenés." — Crearlo / Buscar / Dejar afuera.
 *
 * En toda linea, siempre a la vista: el costo en crudo ("8,78 + 21% IVA =
 * 10,62 USD") y la cantidad, editable con motivo. Y si el producto tiene
 * markup, el precio de venta de antes y el de despues: entrar mercaderia
 * cambia precios, y eso no puede pasar sin que se vea.
 */
import { useState } from "react";
import { ArrowRight, Check, Search, TriangleAlert } from "lucide-react";
import { formatearNumero, type Moneda } from "@tora/shared";

import { Campo, CampoCantidad, mostrarCantidad } from "@/components/catalogo/Campos";
import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { Input } from "@/components/ui/input";
import { aNumero, type Rubro } from "@/lib/tipos";

import { AltaProducto } from "./AltaProducto";
import { cantidadEfectiva, type DatosAlta, type FilaCompra } from "./logica";

export type ModoTarjeta = "DUDOSA" | "SIN_MATCH";

export interface TarjetaLineaProps {
  fila: FilaCompra;
  /** Por defecto se deduce: con producto es DUDOSA, sin producto SIN_MATCH. */
  modo?: ModoTarjeta;
  posicion?: { indice: number; total: number };
  cotizacion: number;
  /** El costo que va a quedar en el producto: en dolares si el proveedor cobra en dolares. */
  costoProducto: { costo: number; moneda: Moneda };
  rubros: ReadonlyArray<Rubro>;
  rubroSugeridoId: number | null;
  guardando: boolean;
  onConfirmarMatch: () => void;
  onBuscarOtro: () => void;
  onCrear: (alta: DatosAlta) => void;
  onIgnorar: () => void;
  onSaltar?: () => void;
  onCantidad: (recibida: number | null, motivo: string | null) => void;
  /** El precio unitario sin IVA mal escrito. Sin esto, el campo no se ofrece. */
  onPrecio?: (costoNeto: number) => void;
}

export function TarjetaLinea({
  fila,
  modo,
  posicion,
  cotizacion,
  costoProducto,
  rubros,
  rubroSugeridoId,
  guardando,
  onConfirmarMatch,
  onBuscarOtro,
  onCrear,
  onIgnorar,
  onSaltar,
  onCantidad,
  onPrecio,
}: TarjetaLineaProps): JSX.Element {
  const modoReal: ModoTarjeta = modo ?? (fila.producto_id !== null ? "DUDOSA" : "SIN_MATCH");
  const [creando, setCreando] = useState(modoReal === "SIN_MATCH");

  const neto = aNumero(fila.costo_neto);
  const iva = aNumero(fila.iva_pct);
  const costo = aNumero(fila.costo_final);

  return (
    <Card padding="normal" className="space-y-4" data-testid={`tarjeta-linea-${fila.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          {posicion ? (
            <p className="text-xs text-muted-foreground">
              Línea {posicion.indice + 1} de {posicion.total} por resolver
            </p>
          ) : null}
          <p className="text-base font-semibold">«{fila.descripcion}»</p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {fila.codigo_externo ? (
              <>
                Código del proveedor <Codigo>{fila.codigo_externo}</Codigo> ·{" "}
              </>
            ) : null}
            {mostrarCantidad(aNumero(fila.cantidad))} u. en el remito
          </p>
        </div>
        <p className="text-sm tabular-nums" data-testid="costo-en-crudo">
          {iva > 0 ? (
            <span className="text-muted-foreground">
              {formatearNumero(neto, 2)} + {formatearNumero(iva, iva % 1 === 0 ? 0 : 1)}% IVA ={" "}
            </span>
          ) : null}
          <Importe monto={costo} moneda={fila.moneda} className="font-semibold" />
          <span className="text-muted-foreground"> c/u</span>
        </p>
      </div>

      {modoReal === "DUDOSA" && !creando ? (
        <PreguntaEsEste
          fila={fila}
          guardando={guardando}
          onSi={onConfirmarMatch}
          onBuscar={onBuscarOtro}
          onCrear={() => setCreando(true)}
          onIgnorar={onIgnorar}
        />
      ) : null}

      {modoReal === "SIN_MATCH" && !creando ? (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <span>No lo tenés en el catálogo.</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => setCreando(true)} disabled={guardando}>
              Crearlo
            </Button>
            <Button type="button" variant="outline" onClick={onBuscarOtro} disabled={guardando}>
              <Search aria-hidden="true" />
              Buscar en el catálogo
            </Button>
            <Button type="button" variant="ghost" onClick={onIgnorar} disabled={guardando}>
              Dejar afuera
            </Button>
          </div>
        </div>
      ) : null}

      {creando ? (
        <AltaProducto
          fila={fila}
          rubros={rubros}
          rubroSugeridoId={rubroSugeridoId}
          cotizacion={cotizacion}
          costoProducto={costoProducto}
          guardando={guardando}
          onCrear={onCrear}
          onCancelar={() => {
            setCreando(false);
            if (modoReal === "SIN_MATCH" && onSaltar) onSaltar();
          }}
        />
      ) : null}

      <CantidadRecibida fila={fila} guardando={guardando} onCantidad={onCantidad} />
      {onPrecio ? <OtroPrecio fila={fila} guardando={guardando} onPrecio={onPrecio} /> : null}

      {onSaltar && !creando ? (
        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onSaltar} disabled={guardando}>
            Saltar por ahora
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/** "¿Es tu «PENDRIVE EXODIA»?" con lo que hace falta para decidir. */
function PreguntaEsEste({
  fila,
  guardando,
  onSi,
  onBuscar,
  onCrear,
  onIgnorar,
}: {
  fila: FilaCompra;
  guardando: boolean;
  onSi: () => void;
  onBuscar: () => void;
  onCrear: () => void;
  onIgnorar: () => void;
}): JSX.Element {
  const costoAnterior = fila.costo_anterior === null ? null : aNumero(fila.costo_anterior);
  const precioActual = fila.precio_actual === null ? null : aNumero(fila.precio_actual);
  const precioResultante = fila.precio_resultante === null ? null : aNumero(fila.precio_resultante);
  const cambiaPrecio =
    fila.precio_manual === null &&
    precioActual !== null &&
    precioResultante !== null &&
    precioActual !== precioResultante;

  return (
    <Card variant="muted" padding="normal" className="space-y-3">
      <p className="text-sm">
        ¿Es tu <strong>«{fila.producto_nombre}»</strong>?
        {fila.match_confianza !== null ? (
          <span className="text-xs text-muted-foreground">
            {" "}
            (parecido {formatearNumero(aNumero(fila.match_confianza) * 100, 0)}%)
          </span>
        ) : null}
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">Código</dt>
          <dd>{fila.codigo_interno ? <Codigo>{fila.codigo_interno}</Codigo> : "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Stock hoy</dt>
          <dd className="tabular-nums">
            {fila.stock_actual === null ? "—" : mostrarCantidad(aNumero(fila.stock_actual))} u.
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Costo hoy</dt>
          <dd>
            {costoAnterior === null || fila.moneda_anterior === null ? (
              "—"
            ) : (
              <Importe monto={costoAnterior} moneda={fila.moneda_anterior} tamano="sm" />
            )}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Vende a</dt>
          <dd>
            {precioActual === null || fila.precio_moneda === null ? (
              "—"
            ) : (
              <Importe monto={precioActual} moneda={fila.precio_moneda} tamano="sm" />
            )}
          </dd>
        </div>
      </dl>

      {cambiaPrecio && fila.precio_moneda ? (
        <Aviso tono="warning" compacto>
          <span className="flex flex-wrap items-center gap-2">
            <span>Si es este, el precio de venta pasa de</span>
            <Importe
              monto={precioActual ?? 0}
              moneda={fila.precio_moneda}
              tamano="sm"
              className="line-through"
            />
            <ArrowRight className="size-3 text-muted-foreground" aria-hidden="true" />
            <Importe
              monto={precioResultante ?? 0}
              moneda={fila.precio_moneda}
              tamano="sm"
              className="font-semibold"
            />
          </span>
        </Aviso>
      ) : fila.precio_manual !== null ? (
        <p className="text-xs text-muted-foreground">
          Tiene el precio puesto a mano: cambia el costo, no el precio de venta.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={onSi} disabled={guardando}>
          <Check aria-hidden="true" />
          Sí, es este
        </Button>
        <Button type="button" variant="outline" onClick={onBuscar} disabled={guardando}>
          <Search aria-hidden="true" />
          No, es otro
        </Button>
        <Button type="button" variant="outline" onClick={onCrear} disabled={guardando}>
          No lo tengo, crearlo
        </Button>
        <Button type="button" variant="ghost" onClick={onIgnorar} disabled={guardando}>
          Dejar afuera
        </Button>
      </div>
    </Card>
  );
}

/**
 * La cantidad viene del remito y se pisa si llego distinto. Pisarla exige un
 * motivo corto, que queda escrito en el ingreso.
 *
 * En una compra a mano no hay remito: la cantidad es la que se tipeó, y si se
 * tipeó mal se corrige y listo, sin motivo y sin «X unidades no llegaron»
 * (decidido por el usuario el 05/11). Eso es `onCorregir`.
 */
export function CantidadRecibida({
  fila,
  guardando,
  onCantidad,
  onCorregir,
}: {
  fila: FilaCompra;
  guardando: boolean;
  onCantidad: (recibida: number | null, motivo: string | null) => void;
  /** Solo en una compra a mano: cambia la cantidad misma. */
  onCorregir?: (cantidad: number) => void;
}): JSX.Element {
  if (onCorregir) {
    return <CorregirCantidad fila={fila} guardando={guardando} onCorregir={onCorregir} />;
  }
  return <CantidadDelRemito fila={fila} guardando={guardando} onCantidad={onCantidad} />;
}

/** La cantidad de una compra a mano: se corrige sin motivo. */
function CorregirCantidad({
  fila,
  guardando,
  onCorregir,
}: {
  fila: FilaCompra;
  guardando: boolean;
  onCorregir: (cantidad: number) => void;
}): JSX.Element {
  const actual = cantidadEfectiva(fila);
  const [editando, setEditando] = useState(false);
  const [cantidad, setCantidad] = useState<number | null>(actual);
  // Sin foco automático, a propósito: el número se marca entero al entrar al
  // campo (ui/input). Con el foco ya puesto por código, el toque del dedo no
  // vuelve a entrar, no marca nada, y «10» sobre «101» quedaba «10101».
  const idCampo = `corregir-cantidad-${fila.id}`;

  if (!editando) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span>
          Entran <strong className="tabular-nums">{mostrarCantidad(actual)}</strong>{" "}
          {actual === 1 ? "unidad" : "unidades"}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setCantidad(actual);
            setEditando(true);
          }}
          disabled={guardando}
        >
          Corregir cantidad
        </Button>
      </div>
    );
  }

  return (
    <Card variant="muted" padding="normal" className="space-y-4">
      <CampoCantidad
        id={idCampo}
        label="Cantidad"
        valor={cantidad}
        onValorChange={setCantidad}
        sufijo="u."
        requerido
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setEditando(false)}
          disabled={guardando}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          disabled={guardando || cantidad === null || cantidad <= 0}
          onClick={() => {
            if (cantidad !== null && cantidad !== actual) onCorregir(cantidad);
            setEditando(false);
          }}
        >
          Guardar cantidad
        </Button>
      </div>
    </Card>
  );
}

/** La cantidad de un remito: lo que llegó distinto lleva motivo. */
function CantidadDelRemito({
  fila,
  guardando,
  onCantidad,
}: {
  fila: FilaCompra;
  guardando: boolean;
  onCantidad: (recibida: number | null, motivo: string | null) => void;
}): JSX.Element {
  const delRemito = aNumero(fila.cantidad);
  const [editando, setEditando] = useState(false);
  const [recibida, setRecibida] = useState<number | null>(cantidadEfectiva(fila));
  const [motivo, setMotivo] = useState(fila.motivo_diferencia ?? "");

  const distinta = recibida !== null && recibida !== delRemito;
  const faltaMotivo = distinta && motivo.trim() === "";

  if (!editando) {
    const efectiva = cantidadEfectiva(fila);
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span>
          Entran <strong className="tabular-nums">{mostrarCantidad(efectiva)}</strong>{" "}
          {efectiva === 1 ? "unidad" : "unidades"}
          {efectiva !== delRemito ? (
            <span className="text-xs text-muted-foreground">
              {" "}
              (el remito dice {mostrarCantidad(delRemito)}: {fila.motivo_diferencia})
            </span>
          ) : null}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setEditando(true)}
          disabled={guardando}
        >
          Llegó otra cantidad
        </Button>
      </div>
    );
  }

  return (
    <Card variant="muted" padding="normal" className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CampoCantidad
          label="Cantidad que llegó"
          valor={recibida}
          onValorChange={setRecibida}
          sufijo="u."
          requerido
          autoFocus
          ayuda={`El remito dice ${mostrarCantidad(delRemito)}.`}
        />
        {distinta ? (
          // Sin motivo, Guardar queda gris: el campo lo dice, y antes no.
          <Campo
            label="Por qué"
            id={`motivo-${fila.id}`}
            requerido
            ayuda="Contá por qué no coincide con el remito."
          >
            <Input
              id={`motivo-${fila.id}`}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Faltó una, vino rota…"
              autoComplete="off"
            />
          </Campo>
        ) : null}
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setEditando(false)}
          disabled={guardando}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          disabled={guardando || recibida === null || recibida <= 0 || faltaMotivo}
          onClick={() => {
            onCantidad(distinta ? recibida : null, distinta ? motivo.trim() : null);
            setEditando(false);
          }}
        >
          Guardar cantidad
        </Button>
      </div>
    </Card>
  );
}

/**
 * El precio unitario como figura en el papel, sin IVA. Antes no habia como
 * corregirlo: el aviso de «precio mal escrito» de la revision pedia revisarlo,
 * y la unica salida era dejar la linea afuera y cargarla de nuevo.
 */
export function OtroPrecio({
  fila,
  guardando,
  onPrecio,
}: {
  fila: FilaCompra;
  guardando: boolean;
  onPrecio: (costoNeto: number) => void;
}): JSX.Element {
  const actual = aNumero(fila.costo_neto);
  const [editando, setEditando] = useState(false);
  const [precio, setPrecio] = useState<number | null>(actual);

  if (!editando) {
    return (
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setPrecio(actual);
            setEditando(true);
          }}
          disabled={guardando}
        >
          Otro precio
        </Button>
      </div>
    );
  }

  const iva = aNumero(fila.iva_pct);
  return (
    <Card variant="muted" padding="normal" className="space-y-4">
      <InputImporte
        label="Precio unitario, como figura"
        valor={precio}
        onValorChange={setPrecio}
        moneda={fila.moneda}
        sinNegativos
        {...(iva > 0
          ? { ayuda: `Sin IVA: el ${formatearNumero(iva, iva % 1 === 0 ? 0 : 1)}% se suma solo.` }
          : {})}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setEditando(false)}
          disabled={guardando}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          disabled={guardando || precio === null || precio < 0 || precio === actual}
          onClick={() => {
            if (precio === null) return;
            onPrecio(precio);
            setEditando(false);
          }}
        >
          Guardar precio
        </Button>
      </div>
    </Card>
  );
}
